import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Send, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import ModelPicker from "@/components/ModelPicker";

function renderMd(text) {
  if (!text) return null;
  return text.split(/\n{2,}/).map((para, i) => {
    const html = para.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>").replace(/\n/g, "<br/>");
    return <p key={i} className="mb-2 last:mb-0" dangerouslySetInnerHTML={{ __html: html }} />;
  });
}

export default function FacilityChat({ facilityId, facilityName }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [model, setModel] = useState("claude-sonnet-5");
  const [busy, setBusy] = useState(false);
  const scroller = useRef(null);

  const load = async () => {
    const { data } = await api.get(`/facilities/${facilityId}/chat`);
    setMsgs(data);
    setTimeout(() => scroller.current?.scrollTo({ top: 1e9, behavior: "smooth" }), 50);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [facilityId]);

  const send = async () => {
    const message = text.trim();
    if (!message || busy) return;
    setBusy(true);
    // Optimistic append
    const optimistic = { msg_id: `tmp-${Date.now()}`, role: "user", content: message, created_at: new Date().toISOString() };
    setMsgs((m) => [...m, optimistic]);
    setText("");
    setTimeout(() => scroller.current?.scrollTo({ top: 1e9, behavior: "smooth" }), 20);
    try {
      const { data } = await api.post(`/facilities/${facilityId}/chat`, { message, model });
      setMsgs((m) => [...m.filter((x) => x.msg_id !== optimistic.msg_id), data.user, data.assistant]);
      setTimeout(() => scroller.current?.scrollTo({ top: 1e9, behavior: "smooth" }), 50);
    } catch (e) {
      setMsgs((m) => m.filter((x) => x.msg_id !== optimistic.msg_id));
      toast.error(e?.response?.data?.detail || "AI response failed");
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    if (!window.confirm("Clear this conversation?")) return;
    await api.delete(`/facilities/${facilityId}/chat`);
    setMsgs([]);
  };

  const suggestions = [
    "Summarize the last 3 visits and any risks.",
    "What follow-ups are overdue or high-priority here?",
    "Draft a talking-points list for my next visit.",
    "What SWOT threats should I address first?",
  ];

  return (
    <Card className="border-slate-200 shadow-none flex flex-col" data-testid="facility-chat" style={{ minHeight: 520 }}>
      <div className="border-b border-slate-200 px-4 py-3 flex items-center justify-between gap-3 bg-white/80 backdrop-blur-md">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-slate-900 flex items-center justify-center"><Sparkles className="w-4 h-4 text-white" /></div>
          <div>
            <div className="font-heading font-bold text-sm">AI facility analyst</div>
            <div className="text-[10px] uppercase tracking-widest font-mono-plex text-slate-500">Grounded on {facilityName}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-44 hidden sm:block"><ModelPicker value={model} onChange={setModel} testId="chat-model" size="sm" /></div>
          <Button variant="ghost" size="icon" onClick={clear} data-testid="chat-clear"><Trash2 className="w-4 h-4 text-slate-400" /></Button>
        </div>
      </div>

      <div ref={scroller} className="flex-1 overflow-y-auto p-4 space-y-4" style={{ maxHeight: 460 }}>
        {msgs.length === 0 && (
          <div className="text-sm text-slate-500 space-y-3">
            <p>Ask anything about this facility. I have its profile, visits, follow-ups, SWOT history and corporate-owner context loaded.</p>
            <div className="flex flex-wrap gap-2">
              {suggestions.map((s) => (
                <button key={s} onClick={() => setText(s)} className="text-xs text-left border border-slate-200 rounded-md px-3 py-2 bg-slate-50 hover:bg-slate-100 transition-colors duration-200" data-testid={`chat-suggest-${s.slice(0, 8)}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {msgs.map((m) => (
          <div key={m.msg_id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} row-enter`}>
            <div className={`max-w-[85%] rounded-md px-4 py-2.5 text-sm ${m.role === "user" ? "bg-slate-900 text-white" : "bg-slate-50 border border-slate-200 text-slate-800"}`}>
              {m.role === "assistant" ? renderMd(m.content) : m.content}
              {m.role === "assistant" && m.model && (
                <div className="mt-1 text-[10px] uppercase tracking-widest font-mono-plex text-slate-400">
                  <Badge variant="outline" className="text-[10px] font-mono-plex">{m.model}</Badge>
                </div>
              )}
            </div>
          </div>
        ))}
        {busy && <div className="text-xs text-slate-400 font-mono-plex">Thinking…</div>}
      </div>

      <div className="border-t border-slate-200 p-3 flex gap-2 items-end bg-white">
        <Textarea
          data-testid="chat-input"
          rows={2}
          value={text}
          placeholder="Ask about this facility…"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }}
          className="resize-none"
        />
        <Button onClick={send} disabled={busy || !text.trim()} className="bg-slate-900 hover:bg-slate-800" data-testid="chat-send">
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </Card>
  );
}
